import type { ConversationEntry, Participant, TranscriptEntry } from '../store/conversationSlice';
import { participantIdOf } from './participantId';

/**
 * A participant's role or name changed by a HUMAN, applied to everything the
 * app already shows (owner, 2026-10-06: a bot call handles a role change
 * exactly as a standalone call does; nothing is ever re-detected). The same
 * rules run on macOS and WPF; services `participant_reclassified`. Pure, so
 * `participantRoleChange.test.ts` pins them.
 *
 * R1 A decision is (role, name?, confirmedAt?): the owner's own roster confirm
 *    on this app, a `participant_reclassified` frame, or a `participants_update`
 *    entry `confirmed` with a newer `confirmedAt` (one this app missed). Which
 *    one wins: (a) `shouldApplyRoleChange`, (b) `snapshotRoleChanges`, (c)
 *    `isEchoOf` (identical on macOS and WPF).
 * R2 Applying one: the roster row, every earlier line of P, P's concern cards
 *    (removed for a salesperson: a salesperson never keeps a customer concern),
 *    P's landmine and competitor alerts (relabelled, never removed).
 * R3 A roster snapshot never changes a decided participant's role or name.
 * R4 Later frames of P take the decision; a card raised by a salesperson is
 *    not shown (it was in flight when the flip happened).
 */

type Role = TranscriptEntry['speaker'];

/** One participant's new identity: `participant_reclassified` in place, or the owner's own confirm. */
export interface ParticipantRoleChange {
  participantId: string;
  role?: Role;
  name?: string;
  /** Epoch ms the decision was made (services clock; this device's for a local confirm). Absent on standalone frames. */
  confirmedAt?: number;
  /** (1) This app's own roster confirm: always applies, never read off the wire. */
  isLocal?: boolean;
}

/** The newest human decision this app holds for a participant. */
export interface ParticipantDecision {
  role?: Role;
  name?: string;
  confirmedAt?: number;
  /**
   * This device's own confirm whose echo has not arrived: its stamp is this
   * device's clock. Only such a decision adopts an echo's stamp (c).
   */
  isLocal?: boolean;
}

export type ParticipantDecisions = Record<string, ParticipantDecision>;

const asRole = (value: unknown): Role | undefined =>
  value === 'salesperson' || value === 'customer' ? value : undefined;

const asName = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const asStamp = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * The in-place shape of `participant_reclassified` (`participantId`, `role`,
 * `name`, `confirmedAt`), or null: a merge shape, no participant, or nothing
 * to change. Recall ids are JSON integers and can be 0.
 */
export const parseParticipantRoleChange = (data: unknown): ParticipantRoleChange | null => {
  const payload = (data ?? {}) as Record<string, unknown>;
  if (payload?.fromParticipantId !== undefined && payload?.fromParticipantId !== null) return null;
  const participantId = participantIdOf(payload?.participantId);
  const role = asRole(payload?.role);
  const name = asName(payload?.name);
  if (!participantId || (!role && !name)) return null;
  const confirmedAt = asStamp(payload?.confirmedAt);
  return {
    participantId,
    ...(role ? { role } : {}),
    ...(name ? { name } : {}),
    ...(confirmedAt !== undefined ? { confirmedAt } : {}),
  };
};

/**
 * (c) Echo adoption, ONLY while the held decision is this device's own
 * unechoed confirm (`isLocal`): a server decision that carries `confirmedAt`
 * and has the held decision's role, where either names no one or both name the
 * same person (the services fill a confirm that named no one with the saved
 * name), is that confirm coming back. The held decision takes the server's
 * stamp (and name), even an older one, and is a server decision from then on:
 * (a) and (b) compare server stamps only, so a stale same-decision entry can
 * never lower the stamp, and device clock skew never hides a later decision.
 */
export const isEchoOf = (held: ParticipantDecision | undefined, change: ParticipantRoleChange): boolean =>
  held?.isLocal === true &&
  !change.isLocal &&
  change.confirmedAt !== undefined &&
  change.role !== undefined &&
  change.role === held.role &&
  (held.name === undefined || change.name === undefined || change.name === held.name);

/**
 * (a) A decision applies unless BOTH it and the held decision carry
 * `confirmedAt` and its stamp is older (a stale echo). Standalone frames carry
 * none and always apply. (1) This app's own confirm always applies: a device
 * clock running behind an adopted server stamp must never make the owner's
 * fresh confirm look older. An echo (c) always applies: re-applying it changes
 * nothing shown, and the held decision adopts the server's stamp.
 */
export const shouldApplyRoleChange = (held: ParticipantDecision | undefined, change: ParticipantRoleChange): boolean =>
  !held ||
  change.isLocal === true ||
  change.confirmedAt === undefined ||
  held.confirmedAt === undefined ||
  change.confirmedAt >= held.confirmedAt ||
  isEchoOf(held, change);

/** The decision held once `change` is applied over `held`: unechoed only when it is this app's own confirm. */
export const decisionAfter = (held: ParticipantDecision | undefined, change: ParticipantRoleChange): ParticipantDecision => ({
  role: change.role ?? held?.role,
  name: change.name ?? held?.name,
  confirmedAt: change.confirmedAt,
  ...(change.isLocal ? { isLocal: true } : {}),
});

/** A roster entry read as a decision. */
const entryAsChange = (entry: Participant): ParticipantRoleChange => ({
  participantId: entry.participantId,
  role: entry.role,
  ...(asName(entry.name) ? { name: entry.name } : {}),
  ...(entry.confirmedAt !== undefined ? { confirmedAt: entry.confirmedAt } : {}),
});

/**
 * (b) The roster entries that are decisions this app missed: `confirmed` AND
 * (no decision held, OR both carry `confirmedAt` and the entry's is newer: a
 * frame missed while the socket was down). Plus (c) an entry that is the held
 * decision's echo, whose stamp the held decision adopts. Any other entry is a
 * snapshot: for a decided participant its role and name are replaced by the
 * decision's (`setParticipants`, R3).
 */
export const snapshotRoleChanges = (entries: Participant[], decisions: ParticipantDecisions): ParticipantRoleChange[] =>
  (entries ?? [])
    .filter((entry) => entry?.participantId && entry?.confirmed === true)
    .map(entryAsChange)
    .filter((change) => {
      // (b) and (c) compare server stamps; an unechoed local confirm is the one exception (c).
      const held = decisions?.[change.participantId];
      if (!held) return true;
      const isNewer =
        change.confirmedAt !== undefined && held.confirmedAt !== undefined && change.confirmedAt > held.confirmedAt;
      return isNewer || isEchoOf(held, change);
    });

/** R2: every line of the participant takes the role, and the name when one is given. */
export const relabelTranscripts = (transcripts: TranscriptEntry[], change: ParticipantRoleChange): TranscriptEntry[] =>
  transcripts.map((row) =>
    row.participantId === change.participantId
      ? { ...row, speaker: change.role ?? row.speaker, speakerName: change.name ?? row.speakerName }
      : row
  );

export interface CardsOutcome {
  entries: ConversationEntry[];
  /** Concern cards removed because their raiser is now a salesperson. */
  removed: ConversationEntry[];
}

/** R2: a salesperson keeps no concern card; a customer's cards take the name. */
export const applyRoleChangeToCards = (entries: ConversationEntry[], change: ParticipantRoleChange): CardsOutcome => {
  const isTheirs = (entry: ConversationEntry) => entry.participantId === change.participantId;
  if (change.role === 'salesperson') {
    return { entries: entries.filter((entry) => !isTheirs(entry)), removed: entries.filter(isTheirs) };
  }
  if (!change.name) return { entries, removed: [] };
  return {
    entries: entries.map((entry) => (isTheirs(entry) ? { ...entry, speakerName: change.name } : entry)),
    removed: [],
  };
};

interface RaisedAlert {
  participantId?: string | number;
  speaker: string;
  speakerName?: string;
}

/** R2: landmine and competitor alerts of the participant take the role and name; never removed. */
export const relabelAlertsOf = <T extends RaisedAlert>(alerts: T[], change: ParticipantRoleChange): T[] =>
  alerts.map((alert) =>
    participantIdOf(alert?.participantId) === change.participantId
      ? { ...alert, speaker: change.role ?? alert.speaker, speakerName: change.name ?? alert.speakerName }
      : alert
  );

/** R4: an alert frame arriving after a decision carries the decided role and name. */
export const withDecision = <T extends RaisedAlert>(alert: T, decisions: ParticipantDecisions): T => {
  const participantId = participantIdOf(alert?.participantId);
  const decision = participantId ? decisions?.[participantId] : undefined;
  if (!decision) return alert;
  return { ...alert, speaker: decision.role ?? alert.speaker, speakerName: decision.name ?? alert.speakerName };
};

/** R4: whether the participant is a salesperson on this app now (its decision, else its roster row). */
export const isSalespersonNow = (
  participantId: string | undefined,
  decisions: ParticipantDecisions,
  participants: Participant[]
): boolean => {
  if (!participantId) return false;
  const role = decisions?.[participantId]?.role ?? participants?.find((p) => p?.participantId === participantId)?.role;
  return role === 'salesperson';
};
